import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Stage C — Mechanical Engineering panel.
 *
 * Sub-stages this panel drives:
 *   C.1 · Pressure Vessel design      (vessels[])
 *   C.2 · Heat Exchanger rating       (heat_exchangers[])
 *   C.3 · Rotating Equipment          (pumps[], compressors[])
 *   C.4 · Storage Tanks               (tanks[])
 *   C.5 · Materials & Corrosion       (materials[], loops[])
 *   C.6 · Auto-generated drawings     (drawings[])
 *   C.7 · Completion + Weight + Hold  (checklist / summary / holds)
 *   C.8 · Completion validator        (report)
 *
 * Everything is driven by the backend endpoints:
 *   GET  /api/projects/{id}/stage-c/results
 *   POST /api/projects/{id}/stage-c/run
 *   GET  /api/projects/{id}/stage-c/drawings
 *   GET  /api/projects/{id}/stage-c/drawings/svg?tag=…
 *   GET  /api/projects/{id}/stage-c/drawings/dxf?tag=…
 */
export default function StageCPanel({ projectId, subStage }) {
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [svg, setSvg] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/stage-c/results`,
      );
      setResults(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/stage-c/run`);
      toast.success("Stage C pipeline completed.");
      await load();
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  const results_map = results?.results || {};
  const substage_data = useMemo(() => {
    if (!results_map) return null;
    switch (subStage) {
      case "C.1":
        return results_map["C.1.1"]?.vessels || [];
      case "C.2":
        return results_map["C.2.1"]?.heat_exchangers || [];
      case "C.3":
        return {
          pumps:       results_map["C.3.1"]?.pumps || [],
          compressors: results_map["C.3.1"]?.compressors || [],
        };
      case "C.4":
        return results_map["C.4.1"]?.tanks || [];
      case "C.5":
        return {
          materials: results_map["C.5.1"]?.materials || [],
          loops:     results_map["C.5.2"]?.loops || [],
        };
      default:
        return null;
    }
  }, [results_map, subStage]);

  return (
    <div data-testid={`stage-c-panel-${subStage}`}
         className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-950 p-4">
        <div>
          <div className="text-base font-semibold text-neutral-100">
            Stage C · Mechanical Engineering
          </div>
          <div className="text-xs text-neutral-500">
            Sub-stage {subStage} · powered by C.1-C.8 solver + auto CAD generator
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="stage-c-run"
            onClick={run}
            disabled={running}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50">
            {running ? "Running…" : (results?.cached ? "Re-run pipeline" : "Run C.1-C.8 pipeline")}
          </button>
          <button
            data-testid="stage-c-refresh"
            onClick={load}
            className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-xs text-neutral-200 hover:bg-neutral-800">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="stage-c-error"
             className="rounded-md border border-red-800 bg-red-950/60 p-3 text-xs text-red-200">
          {error}
        </div>
      )}
      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-900 p-3 text-xs text-neutral-400">
          Loading Stage C results…
        </div>
      )}

      {!loading && !results?.cached && (
        <div data-testid="stage-c-empty"
             className="rounded-md border border-amber-800 bg-amber-950/40 p-3 text-xs text-amber-100">
          No cached Stage C results. Click <b>Run C.1-C.8 pipeline</b> to
          generate mechanical calculations + drawings for all listed equipment.
        </div>
      )}

      {results?.cached && (
        <>
          <StageCSummary results_map={results_map} />
          <StageCSubStagePanel
            subStage={subStage}
            data={substage_data}
            projectId={projectId}
            drawings={(results_map["C.6.1"]?.drawings) || []}
            selectedTag={selectedTag}
            setSelectedTag={(tag) => {
              setSelectedTag(tag);
              setSvg(null);
              // preview drawing
              if (tag) {
                api.get(
                  `/projects/${projectId}/stage-c/drawings/svg?tag=${encodeURIComponent(tag)}`,
                  { responseType: "text" },
                ).then((r) => setSvg(r.data))
                  .catch((e) =>
                    toast.error(formatErr(e.response?.data?.detail) || e.message));
              }
            }}
            svg={svg}
          />
        </>
      )}
    </div>
  );
}

// ─────────────────── summary strip ───────────────────
function StageCSummary({ results_map }) {
  const s = (code, key) => (results_map[code]?.[key] || []).length;
  const stats = [
    { label: "Vessels",       n: s("C.1.1", "vessels") },
    { label: "Heat Xchgrs",   n: s("C.2.1", "heat_exchangers") },
    { label: "Pumps",         n: s("C.3.1", "pumps") },
    { label: "Compressors",   n: s("C.3.1", "compressors") },
    { label: "Tanks",         n: s("C.4.1", "tanks") },
    { label: "Materials",     n: s("C.5.1", "materials") },
    { label: "Corr. loops",   n: s("C.5.2", "loops") },
    { label: "Drawings",      n: s("C.6.1", "drawings") },
    { label: "Holds",         n: s("C.7.3", "holds") },
  ];
  return (
    <div data-testid="stage-c-summary"
         className="grid grid-cols-3 gap-2 sm:grid-cols-9">
      {stats.map((x) => (
        <div key={x.label}
             className="rounded-md border border-neutral-800 bg-neutral-950 p-2 text-center">
          <div className="text-lg font-semibold text-neutral-100">{x.n}</div>
          <div className="text-[10px] uppercase tracking-wide text-neutral-500">
            {x.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────── sub-stage panel ───────────────────
function StageCSubStagePanel({ subStage, data, projectId, drawings,
                                selectedTag, setSelectedTag, svg }) {
  if (subStage === "C.1") {
    return <EquipmentGrid items={data || []} type="vessel" drawings={drawings}
                              projectId={projectId} selectedTag={selectedTag}
                              setSelectedTag={setSelectedTag} svg={svg} />;
  }
  if (subStage === "C.2") {
    return <EquipmentGrid items={data || []} type="hx" drawings={drawings}
                              projectId={projectId} selectedTag={selectedTag}
                              setSelectedTag={setSelectedTag} svg={svg} />;
  }
  if (subStage === "C.3") {
    return (
      <div className="space-y-4">
        <EquipmentGrid items={data?.pumps || []} type="pump" drawings={drawings}
                          projectId={projectId} selectedTag={selectedTag}
                          setSelectedTag={setSelectedTag} svg={svg} />
        <EquipmentGrid items={data?.compressors || []} type="compressor"
                          drawings={drawings} projectId={projectId}
                          selectedTag={selectedTag}
                          setSelectedTag={setSelectedTag} svg={svg} />
      </div>
    );
  }
  if (subStage === "C.4") {
    return <EquipmentGrid items={data || []} type="tank" drawings={drawings}
                              projectId={projectId} selectedTag={selectedTag}
                              setSelectedTag={setSelectedTag} svg={svg} />;
  }
  if (subStage === "C.5") {
    return (
      <div className="space-y-4">
        <MaterialsGrid items={data?.materials || []} />
        <CorrosionLoopsTable loops={data?.loops || []} />
      </div>
    );
  }
  return null;
}

// ─────────────────── equipment grid ───────────────────
function EquipmentGrid({ items, type, drawings, projectId, selectedTag,
                          setSelectedTag, svg }) {
  const label = { vessel: "Vessels", hx: "Heat exchangers",
                    pump: "Pumps", compressor: "Compressors", tank: "Tanks" }[type];
  if (!items.length) {
    return (
      <div className="rounded-md border border-neutral-800 bg-neutral-950 p-3 text-xs text-neutral-500">
        No {label?.toLowerCase()} in this stage.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-neutral-200">{label}</div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {items.map((eq) => (
          <EquipmentCard key={eq.tag} eq={eq} type={type}
                            projectId={projectId}
                            hasDrawing={!!drawings.find((d) => d.tag === eq.tag)}
                            selectedTag={selectedTag}
                            setSelectedTag={setSelectedTag} />
        ))}
      </div>
      {selectedTag && svg && (
        <DrawingPreview svg={svg} tag={selectedTag} projectId={projectId} />
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-neutral-900 py-0.5 text-xs">
      <span className="text-neutral-500">{k}</span>
      <span className="font-medium text-neutral-100">{String(v ?? "-")}</span>
    </div>
  );
}

function EquipmentCard({ eq, type, projectId, hasDrawing, selectedTag,
                          setSelectedTag }) {
  const dc = eq.design_conditions || {};
  const sh = eq.shell || {};
  const hd = eq.head || {};
  const mawp = eq.MAWP || {};
  const thermal = eq.thermal_design || eq.performance || {};
  const hyd = eq.hydraulics || {};
  const geom = eq.geometry || {};
  const wt = eq.weight || {};
  const mdmt = eq.mdmt || {};

  const rows = type === "vessel" ? [
    ["Code", eq.code], ["Material", dc.material],
    ["Design P (barg)", dc.design_pressure_barg],
    ["Design T (°C)", dc.design_temperature_C],
    ["MDMT (°C)", mdmt.adjusted_MDMT_C ?? dc.MDMT_C],
    ["MAWP (barg)", mawp.MAWP_barg],
    ["Shell thk (mm)", sh.t_selected_mm],
    ["Head thk (mm)", hd.t_selected_mm],
    ["Empty wt (kg)", wt.empty_kg],
    ["Skirt?", eq.skirt?.applicable ? "Yes" : "No"],
  ] : type === "hx" ? [
    ["TEMA", geom.tema_type || eq.tema_auto?.tema_type],
    ["Duty (kW)", thermal.duty_kW],
    ["LMTD (°C)", thermal.LMTD_C],
    ["U (W/m²K)", thermal.U_design_WmK ?? thermal.U_WmK],
    ["Area (m²)", thermal.area_m2],
    ["Shell dP (bar)", eq.shell_side_dP?.dP_shell_bar],
    ["Overdesign", (thermal.overdesign_pct ?? "-") + " %"],
    ["Fouling m²K/W", eq.fouling?.resistance_m2KW],
  ] : type === "pump" ? [
    ["API type", eq.selection?.type],
    ["Rated Q (m³/h)", hyd.rated_flow_m3h],
    ["Head (m)", hyd.differential_head_m],
    ["Efficiency %", hyd.pump_efficiency_pct],
    ["Motor kW", hyd.motor_rated_kW],
    ["NPSHa (m)", eq.npsha_rigorous?.NPSHa_m],
    ["Seal", eq.seal_selection?.api_plan],
    ["Driver", eq.driver?.driver_type],
    ["Op. Q (m³/h)", eq.system_curve?.operating_point?.Q_m3h],
  ] : type === "compressor" ? [
    ["Stages", eq.multistage_analysis?.n_stages],
    ["Ratio/stage", eq.multistage_analysis?.ratio_per_stage],
    ["Shaft kW", eq.performance?.shaft_power_kW],
    ["Discharge T (°C)", eq.performance?.discharge_T_C],
    ["Driver", eq.driver?.driver_type],
    ["Voltage", eq.driver?.voltage_level],
    ["Intercool duty (kW)",
        eq.multistage_analysis?.total_intercooler_duty_kW],
  ] : [
    ["Code", eq.code],
    ["D (m)", geom.diameter_m],
    ["H (m)", geom.height_m],
    ["Vol (m³)", eq.volume_m3],
    ["Roof", eq.roof?.type],
    ["Product", eq.service],
    ["Seismic Z", eq.seismic?.seismic_Z],
    ["Sloshing (m)", eq.seismic?.sloshing_wave_m],
    ["Wind (kN)", eq.wind_load?.wind_shear_kN],
    ["Annular (mm)", eq.annular_plate?.annular_plate_thickness_mm],
  ];

  return (
    <div data-testid={`c-eq-card-${eq.tag}`}
         className="rounded-lg border border-neutral-800 bg-neutral-950 p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-neutral-100">{eq.tag}</div>
          <div className="text-xs text-neutral-500">{eq.name || eq.service}</div>
        </div>
        <div className="flex gap-1">
          {hasDrawing && (
            <>
              <button
                data-testid={`c-eq-preview-${eq.tag}`}
                onClick={() => setSelectedTag(eq.tag)}
                className={`rounded-md px-2 py-1 text-[11px] ${
                  selectedTag === eq.tag
                    ? "bg-emerald-600 text-white"
                    : "border border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800"
                }`}>
                Preview
              </button>
              <a
                data-testid={`c-eq-dxf-${eq.tag}`}
                href={`${API_BASE}/projects/${projectId}/stage-c/drawings/dxf?tag=${encodeURIComponent(eq.tag)}`}
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-[11px] text-neutral-300 hover:bg-neutral-800">
                DXF ↓
              </a>
            </>
          )}
        </div>
      </div>
      <div>
        {rows.map(([k, v]) => <KV key={k} k={k} v={v} />)}
      </div>
    </div>
  );
}

function DrawingPreview({ svg, tag, projectId }) {
  return (
    <div data-testid="c-drawing-preview"
         className="rounded-lg border border-neutral-800 bg-neutral-950 p-3">
      <div className="mb-2 flex items-center justify-between text-xs">
        <div className="font-semibold text-neutral-200">Preview · {tag}</div>
        <a
          href={`${API_BASE}/projects/${projectId}/stage-c/drawings/dxf?tag=${encodeURIComponent(tag)}`}
          className="rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] text-neutral-300 hover:bg-neutral-800">
          Download AutoCAD DXF
        </a>
      </div>
      <div className="rounded-md bg-white p-2"
           dangerouslySetInnerHTML={{ __html: svg }} />
    </div>
  );
}

// ─────────────────── C.5 tables ───────────────────
function MaterialsGrid({ items }) {
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-neutral-200">Materials & Corrosion Assessment</div>
      <div className="overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-900 text-neutral-400">
            <tr>
              {["Tag","MOC","T °C","Corr allowance","Remaining life",
                 "CRA recommendation","Nelson curve","WPS"].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.tag}
                  className="border-t border-neutral-800 text-neutral-200">
                <td className="px-2 py-1.5">{it.tag}</td>
                <td className="px-2 py-1.5">{it.MOC || it.material || "-"}</td>
                <td className="px-2 py-1.5">{it.T_C ?? "-"}</td>
                <td className="px-2 py-1.5">{it.CA_mm ?? it.corrosion_allowance_mm ?? "-"}</td>
                <td className="px-2 py-1.5">{it.remaining_life_years ?? it.remaining_life ?? "-"}</td>
                <td className="px-2 py-1.5">{it.cra_recommendation?.recommendation || "-"}</td>
                <td className="px-2 py-1.5">
                  {it.nelson_curve?.applicable === false
                    ? "N/A"
                    : (it.nelson_curve?.HTHA_check || "-")}
                </td>
                <td className="px-2 py-1.5">{it.wps_summary?.process || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CorrosionLoopsTable({ loops }) {
  if (!loops.length) return null;
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-neutral-200">Corrosion Loop Register</div>
      <div className="overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-900 text-neutral-400">
            <tr>
              {["Loop","Material","Environment","T range (°C)",
                 "Rate mm/yr","Monitoring","Equipment / Lines"].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loops.map((l) => (
              <tr key={l.loop_id}
                  className="border-t border-neutral-800 text-neutral-200">
                <td className="px-2 py-1.5 font-semibold">{l.loop_id}</td>
                <td className="px-2 py-1.5">{l.material}</td>
                <td className="px-2 py-1.5">{l.environment}</td>
                <td className="px-2 py-1.5">{l.T_range_C}</td>
                <td className="px-2 py-1.5">{l.expected_rate_mmpy}</td>
                <td className="px-2 py-1.5">{l.monitoring_method}</td>
                <td className="px-2 py-1.5 text-[10px]">
                  {(l.equipment || []).join(", ")}
                  {(l.lines || []).length ? " · " + l.lines.length + " lines" : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
